import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { DSSPaymentFrequency } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { ListBox } from '@flos/react-ui';
import { useRecoilValue } from 'recoil';
import { externalCasesAtom } from '../../../recoil/atoms/externalCasesAtom';
import ShyTitle from './ShyTitle';
import { paymentFrequencyMap, periodicityUnit } from '../../../utils/currencyUtil';
import {
    convertLocalDateDTOToDate,
    convertLocalDateToString,
    coverableEffectiveDate,
    isDateInThePast
} from '../../../utils/dateUtil';
import { coverableToExternalCase } from '../../../utils/externalCasesUtil';
import Currency from '../../../shared_components/Currency/Currency';
import { startDateAtom } from '../../../recoil/atoms/startDateAtom';
import { businessFlowSelector } from '../../../recoil/selectors/getBusinessFlowSelector';
import { useHippoText } from '../../../hooks/useHippoText';

const ProductAndPrice = ({
    coverable,
    paymentFrequency,
    isPaymentYearly,
    shouldHideDetails = false
}: {
    coverable: CoverableDTO;
    paymentFrequency: DSSPaymentFrequency;
    isPaymentYearly: boolean;
    shouldHideDetails?: boolean;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const allExternalCases = useRecoilValue(externalCasesAtom);
    const startDateDTOs = useRecoilValue(startDateAtom);
    const startDateDTO = startDateDTOs?.find((dto) => dto.fixedId === coverable.fixedId);
    const externalCase = coverableToExternalCase(coverable, allExternalCases);
    const businessFlow = useRecoilValue(businessFlowSelector);
    const isMPSBusinessFlow = businessFlow === 'MPS';
    const startDate =
        externalCase?.cancellationDate && externalCase.otherCompany
            ? externalCase?.cancellationDate
            : externalCase?.startDate;

    if (shouldHideDetails) {
        return (
            <div className="u-spacing-inset-m--stretch-max">
                <ListBox
                    theme="transparent"
                    title={coverable.flavourName ?? ''}
                    subtitle={coverable.name}
                />
            </div>
        );
    }

    return (
        <div className="u-spacing-inset-m--stretch-max">
            <ListBox
                theme={'transparent'}
                title={ShyTitle(coverable.flavourName ?? '')}
                subtitle={coverable.name}
                action={
                    <h6 className="u-text-right u-Nowrap">
                        <Currency
                            price={
                                paymentFrequency === paymentFrequencyMap.monthly
                                    ? coverable.monthlyPrice?.amount ?? 0
                                    : coverable.yearlyPrice?.amount ?? 0
                            }
                            paymentFrequencyPostfix={periodicityUnit[paymentFrequency]}
                        />
                    </h6>
                }
            />
            {!coverable.willSpinOff && (
                <>
                    {startDateDTO?.effectiveDate && (
                        <p className="paragraph--small u-color-primary-1-base u-spacing-stack-xs">
                            Forsikringen gælder fra{' '}
                            {convertLocalDateToString(
                                convertLocalDateDTOToDate(startDateDTO.effectiveDate)
                            )}
                        </p>
                    )}
                    {startDate && (
                        <p className="paragraph--small u-color-primary-1-base u-spacing-stack-xs">
                            {isDateInThePast(convertLocalDateDTOToDate(startDate)) &&
                            !isMPSBusinessFlow
                                ? ''
                                : getHippoText('DSS-SIDEBAR-EFFECTIVE-DATE', {
                                      date:
                                          convertLocalDateToString(
                                              convertLocalDateDTOToDate(
                                                  coverableEffectiveDate(coverable, startDate)
                                              )
                                          ) ?? ''
                                  })}
                        </p>
                    )}
                    {!startDate && isMPSBusinessFlow && coverable.effectiveDate && (
                        <p className="paragraph--small u-color-primary-1-base u-spacing-stack-xs">
                            Forsikringen gælder fra{' '}
                            {convertLocalDateToString(
                                convertLocalDateDTOToDate(coverable.effectiveDate)
                            ) ?? ''}
                        </p>
                    )}
                    {externalCase?.cancellationDate && externalCase.otherCompany && (
                        <p className="paragraph--small u-color-secondary-4-medium">
                            (samtidig opsiger vi forsikringen i {externalCase.otherCompany.name} fra{' '}
                            {convertLocalDateToString(
                                convertLocalDateDTOToDate(externalCase.cancellationDate)
                            )}
                            )
                        </p>
                    )}
                </>
            )}
        </div>
    );
};

export default ProductAndPrice;
